/**
 * Combat Engine Types
 * Self-contained types for the pure client-side combat engine.
 * No imports from server code or shared types.
 */

import type { PositionId, MapTemplate, RangeBand } from './grid';
import type { DicePoolProbabilities } from './dice';

// ============================================================================
// DICE POOL PREVIEW
// ============================================================================

export interface DicePoolPreview {
  poolSize: number;
  probabilities: DicePoolProbabilities;
}

// ============================================================================
// BASE TYPES
// ============================================================================

export type Stat = 'brawn' | 'grace' | 'wits' | 'spirit';
export type CombatActionType = 'attack' | 'defend' | 'skill' | 'magic' | 'move' | 'item';
export type ReactionType = 'block' | 'dodge' | 'parry';

// ============================================================================
// SKILL SYSTEM TYPES
// ============================================================================

export type SkillCategory = 'offensive' | 'defensive' | 'movement' | 'utility';

export type SkillTargeting = 'single_enemy' | 'single_ally' | 'self' | 'all_enemies' | 'all_allies';

export interface SkillCooldown {
  type: 'per_round' | 'per_combat';
  maxUses: number;
}

export type ConditionType =
  | 'shielded'
  | 'inspired'
  | 'fortified'
  | 'taunted'
  | 'slowed'
  | 'weakened'
  | 'burn'
  | 'stunned'
  | 'riposte_ready'
  | 'shadow_advantage';

export type SkillEffect =
  | { type: 'damage'; baseDamage: number }
  | { type: 'heal'; value: number }
  | { type: 'apply_condition'; condition: ConditionType; duration: number; target: 'self' | 'target' }
  | { type: 'remove_condition'; condition: ConditionType; target: 'self' | 'target' }
  | { type: 'force_move'; direction: 'close' | 'retreat' }
  | { type: 'self_move'; direction: 'close' | 'retreat' }
  | { type: 'restore_resolve'; value: number }
  | { type: 'restore_ap'; value: number };

export interface SkillDefinition {
  id: string;
  name: string;
  description: string;
  category: SkillCategory;
  stat: Stat;
  targeting: SkillTargeting;
  range: 'melee' | 'close' | 'ranged' | 'self';
  apCost: number;
  resolveCost: number;
  cooldown: SkillCooldown;
  effects: SkillEffect[];
  keywords: string[];
  requiresRoll: boolean;
  generatesThreat: boolean;
  threatProperties?: {
    canBeBlocked: boolean;
    canBeDodged: boolean;
    canBeParried: boolean;
  };
}

export interface Stats {
  brawn: number;
  grace: number;
  wits: number;
  spirit: number;
}

// ============================================================================
// PARTICIPANTS
// ============================================================================

export interface CombatParticipant {
  id: string;
  type: 'player' | 'npc';
  name: string;
  initiative: number;
  vitals: number;
  vitalsMax: number;
  resolve: number;
  resolveMax: number;
  conditions: string[];
  hasActed: boolean;
  isDefeated: boolean;
  tags?: string[];
  description?: string;
}

export interface APCombatParticipant extends CombatParticipant {
  apMax: number;
  apRemaining: number;
  hasCommitted: boolean;
  stats: Stats;
  magicDiscipline?: MagicDiscipline;
  skillIds: string[];
  /** Passive defense value — attackers need (defense + 1) dice showing 4+ to hit */
  defense: number;
  /** Tiles moved per 1 AP spent on movement */
  speed: number;
}

// ============================================================================
// ACTIONS & REACTIONS
// ============================================================================

export interface CommittedAction {
  actionId: string;
  actionType: CombatActionType;
  targetId?: string;
  targetPosition?: PositionId;
  apSpent: number;
  resolveSpent?: number;
}

export interface CommittedReaction {
  threatId: string;
  reactionType: ReactionType;
  apSpent: number;
  resolveSpent?: number;
}

export interface RoundCommit {
  participantId: string;
  actions: CommittedAction[];
  reactions: CommittedReaction[];
}

// ============================================================================
// THREATS
// ============================================================================

export interface IncomingThreat {
  id: string;
  attackerId: string;
  attackerName: string;
  actionId: string;
  actionName: string;
  apSpent: number;
  estimatedDamage: 'light' | 'moderate' | 'heavy' | 'devastating';
  canBeBlocked: boolean;
  canBeDodged: boolean;
  canBeParried: boolean;
}

// ============================================================================
// RESOLUTION
// ============================================================================

export interface ResolutionResult {
  actorId: string;
  actorName: string;
  actionType: string;
  targetId?: string;
  targetName?: string;
  diceResult?: {
    pool: number;
    rolls: number[];
    highest: number;
    result: 'critical' | 'success' | 'struggle' | 'fail';
    deflected?: boolean;
  };
  damage?: number;
  damageBlocked?: number;
  dodged?: boolean;
  parried?: boolean;
  effects: string[];
  isDefeat?: boolean;
}

export interface RoundResolution {
  roundNumber: number;
  results: ResolutionResult[];
  defeatedParticipants: string[];
  combatEnded: boolean;
  outcome?: 'victory' | 'defeat' | 'fled' | null;
}

export interface CombatLogEntry {
  round: number;
  actorId: string;
  actorName: string;
  action: string;
  targetId?: string;
  targetName?: string;
  result: string;
  diceResult?: {
    roll: number;
    type: string;
  };
  damage?: number;
  timestamp: string;
}

// ============================================================================
// AVAILABLE ACTIONS (pre-computed for UI)
// ============================================================================

export interface MoveTarget {
  positionId: PositionId;
  apCost: number;
  label: string;
}

export interface AvailableAction {
  id: string;
  name: string;
  type: CombatActionType;
  apCost: number;
  description: string;
  stat: Stat;
  range: 'melee' | 'close' | 'ranged' | 'self' | 'all';
  resolveCost: number;
  keywords: string[];
  /** Dice pool preview for untargeted actions (self-buff spells, etc.) */
  dicePool?: DicePoolPreview;
  targets?: Array<{
    id: string;
    name: string;
    position: PositionId;
    rangeBand: RangeBand;
    /** Per-target dice pool (accounts for cover, elevation, defense) */
    dicePool?: DicePoolPreview;
  }>;
  moveTargets?: MoveTarget[];
}

export interface AvailableReactionSet {
  threatId: string;
  threat: IncomingThreat;
  options: Array<{
    type: ReactionType;
    apCost: number;
    resolveCost: number;
    description: string;
    dicePool?: DicePoolPreview;
  }>;
}

// ============================================================================
// ENEMY & PLAYER DEFINITIONS (for creating combat)
// ============================================================================

export interface EnemyDefinition {
  name: string;
  race?: string;
  description?: string;
  vitals: number;
  vitalsMax?: number;
  resolve?: number;
  resolveMax?: number;
  tags: string[];
  startPosition: PositionId;
  /** Passive defense value (if omitted, inferred from tags like 'armored') */
  defense?: number;
}

export interface PlayerDefinition {
  name: string;
  vitals: number;
  vitalsMax: number;
  resolve: number;
  resolveMax: number;
  stats: Stats;
  tags?: string[];
  equipment?: string[];
  magicDiscipline?: MagicDiscipline;
  skillIds?: string[];
  /** Passive defense value (default 0) */
  defense?: number;
}

// ============================================================================
// ENGINE STATE
// ============================================================================

export type EnginePhase = 'player_commit' | 'npc_commit' | 'resolving' | 'round_ended' | 'ended';

export interface CombatEngineState {
  phase: EnginePhase;
  roundNumber: number;
  participants: APCombatParticipant[];
  positions: Record<string, PositionId>;
  mapTemplate: MapTemplate;
  availablePositions: PositionId[];
  terrain: Record<string, string>;  // positionId → terrain catalog key
  pendingCommits: RoundCommit[];
  unresolvedAttacks: RoundCommit[];  // Attack actions from previous round awaiting resolution
  pendingDamage: Record<string, number>;  // participantId → estimated total pending damage
  incomingThreats: Record<string, IncomingThreat[]>;
  lastResolution: RoundResolution | null;
  combatLog: ResolutionResult[];
  outcome: 'victory' | 'defeat' | 'fled' | null;
  availableActions: Record<string, AvailableAction[]>;
  availableReactions: Record<string, AvailableReactionSet[]>;
  skillCooldowns: Record<string, Record<string, number>>; // participantId → skillId → uses remaining
}

export interface CombatConfig {
  players: PlayerDefinition[];
  enemies: EnemyDefinition[];
  customSkills?: SkillDefinition[];
  mapTemplate?: MapTemplate;
  playerStartPosition?: PositionId;
  sceneMap?: string;  // Scene map ID (overrides mapTemplate if provided)
}

export interface PlayerCommitInput {
  participantId: string;
  actions: CommittedAction[];
  reactions: CommittedReaction[];
}

// ============================================================================
// AP COST CONSTANTS
// ============================================================================

export const AP_COSTS = {
  block: 1,
  dodge: 2,
  parry: 2,
  attack: 2,
  move: 1,
  run: 2,
  cast: 3,
  item: 1,
  flee: 3,
  defend: 1,
  skill: 2,
} as const;

export const RESOLVE_COSTS = {
  block: 0,
  dodge: 1,
  parry: 1,
  attack: 0,
  move: 0,
  run: 0,
  cast: 3,
  item: 0,
  flee: 0,
  defend: 0,
  skill: 0,
} as const;

// ============================================================================
// MAGIC DISCIPLINE DEFINITIONS
// ============================================================================

export type MagicDiscipline = 'kinetic' | 'ember' | 'vitality' | 'veil' | 'influence' | 'none';

export interface SpellDefinition {
  id: string;
  name: string;
  discipline: MagicDiscipline;
  range: 'melee' | 'close' | 'ranged' | 'self';
  stat: Stat;
  apCost: number;
  resolveCost: number;
  description: string;
  keywords: string[];
}

export const SPELL_DEFINITIONS: Record<Exclude<MagicDiscipline, 'none'>, SpellDefinition> = {
  kinetic: {
    id: 'spell_force_blast',
    name: 'Force Blast',
    discipline: 'kinetic',
    range: 'ranged',
    stat: 'spirit',
    apCost: AP_COSTS.cast,
    resolveCost: RESOLVE_COSTS.cast,
    description: 'Hurl a blast of force at a target, dealing damage and pushing them back',
    keywords: ['magic', 'ranged', 'push'],
  },
  ember: {
    id: 'spell_firebolt',
    name: 'Firebolt',
    discipline: 'ember',
    range: 'ranged',
    stat: 'spirit',
    apCost: AP_COSTS.cast,
    resolveCost: RESOLVE_COSTS.cast,
    description: 'Launch a bolt of fire that burns the target over time',
    keywords: ['magic', 'ranged', 'burn'],
  },
  vitality: {
    id: 'spell_mend_wounds',
    name: 'Mend Wounds',
    discipline: 'vitality',
    range: 'close',
    stat: 'spirit',
    apCost: AP_COSTS.cast,
    resolveCost: RESOLVE_COSTS.cast,
    description: 'Channel healing energy to restore vitals, at the cost of maximum resolve',
    keywords: ['magic', 'heal', 'self'],
  },
  veil: {
    id: 'spell_shadow_step',
    name: 'Shadow Step',
    discipline: 'veil',
    range: 'self',
    stat: 'spirit',
    apCost: AP_COSTS.cast,
    resolveCost: 2, // Veil discount
    description: 'Teleport through shadows, gaining advantage on your next attack',
    keywords: ['magic', 'teleport', 'stealth'],
  },
  influence: {
    id: 'spell_dominate',
    name: 'Dominate',
    discipline: 'influence',
    range: 'ranged',
    stat: 'spirit',
    apCost: AP_COSTS.cast,
    resolveCost: RESOLVE_COSTS.cast,
    description: 'Overwhelm a target\'s will, stunning them for one round',
    keywords: ['magic', 'ranged', 'stun'],
  },
};
